import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Dashboard6Styles } from "../components/ModalUI";
import { useRateLimit } from "../utils/rateLimit";
import {
  tenantLogin,
  tenantFirstLoginStart,
  tenantFirstLoginVerifyOtp,
  tenantFirstLoginSetPassword,
  forgotPasswordStart,
  forgotPasswordVerifyOtp,
  forgotPasswordReset,
  validatePassword,
  clearTenantSession,
  isTenantAuthenticated,
} from "../tenant/pages/TenantAuth";

// ==========================================
// AUTH UTILITIES & API
// ==========================================
const DEV_PROXY_BASE = "/api";
const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = process.env.NODE_ENV === "development" ? DEV_PROXY_BASE : PROD_API_BASE;
const SESSION_KEY = "zangon_session";

function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function getSession() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
  } catch {
    return null;
  }
}

function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem("auth_token");
  sessionStorage.removeItem("zangon_session_active");
  try {
    const remember = localStorage.getItem("zangon_remember_me");
    if (remember !== "true") {
      localStorage.removeItem("zangon_remembered_email");
      localStorage.removeItem("zangon_remember_me");
    }
  } catch {}
}

export function getAccessToken() {
  return getSession()?.accessToken || "";
}

export function getRefreshToken() {
  return getSession()?.refreshToken || "";
}

export function getAccessTokenExpiresAt() {
  return getSession()?.accessTokenExpiresAt || "";
}

export function saveLoginSession(payload) {
  setSession(payload);
  localStorage.setItem("auth_token", payload?.accessToken || "");
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 60000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    return res;
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally {
    clearTimeout(id);
  }
}

function getJwtRoles(token) {
  try {
    const part = String(token).split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(part));
    return Object.keys(claims)
      .filter((k) => /role|usertype/i.test(k))
      .map((k) => claims[k]);
  } catch {
    return [];
  }
}

export async function adminLogin({ email, password, rememberMe = true }) {
  const res = await fetchWithTimeout(`${API_BASE}/auth/admin-login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ email, password }),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;

  if (!res.ok) {
    const msg =
      json?.message ||
      json?.error ||
      json?.statusText ||
      text ||
      `Login failed (${res.status})`;
    throw new Error(msg);
  }

  const session = json?.data;

  if (!session?.accessToken || !session?.refreshToken) {
    throw new Error("Login succeeded but tokens were not returned.");
  }

  // Never store a tenant/occupant token as an admin session — let the caller fall back to tenant login.
  const roleText = JSON.stringify([session.role, session.roles, session.userType, getJwtRoles(session.accessToken)]).toLowerCase();
  if (/tenant|occupant/.test(roleText)) {
    throw new Error("This account is not an admin account.");
  }

  try {
    if (rememberMe) {
      localStorage.setItem("zangon_remember_me", "true");
      localStorage.setItem("zangon_remembered_email", email.trim());
    } else {
      localStorage.setItem("zangon_remember_me", "false");
      localStorage.removeItem("zangon_remembered_email");
    }
    sessionStorage.setItem("zangon_session_active", "true");
  } catch {}

  saveLoginSession(session);

  return { ok: true, session, message: json?.message || "Login successful." };
}

export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token. Please sign in again.");

  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;

  if (!res.ok) {
    const msg = json?.message || json?.statusText || `Refresh failed (${res.status})`;
    throw new Error(msg);
  }

  const prev = getSession() || {};
  const incoming = json?.data ?? json ?? {};
  const next = { ...prev, ...incoming };

  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");

  return next;
}


// ==========================================
// UNIFIED LOGIN (ADMIN OR TENANT)
// ==========================================
// Emails try the admin endpoint first and fall back to the tenant endpoint;
// phone numbers can only belong to tenants.
async function unifiedLogin({ identifier, password, rememberMe }) {
  const id = identifier.trim();
  if (id.includes("@")) {
    try {
      const res = await adminLogin({ email: id, password, rememberMe });
      clearTenantSession();
      return { ...res, redirect: "/dashboard" };
    } catch (adminErr) {
      try {
        const res = await tenantLogin({ emailOrPhone: id, password, rememberMe });
        clearSession();
        return { ...res, redirect: "/tenant/dashboard" };
      } catch {
        throw adminErr;
      }
    }
  }
  const res = await tenantLogin({ emailOrPhone: id, password, rememberMe });
  clearSession();
  return { ...res, redirect: "/tenant/dashboard" };
}

// ==========================================
// SIGN IN COMPONENT
// ==========================================
const SIGN_IN_IMAGES = [
  "/assets/media/side1.jpg",
  "/assets/media/side2.jpg",
  "/assets/media/side3.jpg",
];
const LAST_IMAGE_KEY = "zangon_signin_last_image";

// Random image that never repeats the one shown on the previous visit.
function pickSignInImage() {
  let last = "";
  try { last = sessionStorage.getItem(LAST_IMAGE_KEY) || ""; } catch {}
  const pool = SIGN_IN_IMAGES.filter((img) => img !== last);
  const list = pool.length > 0 ? pool : SIGN_IN_IMAGES;
  return list[Math.floor(Math.random() * list.length)];
}

const PASSWORD_RULES = [
  { label: "At least 6 characters", test: (p) => p.length >= 6 },
  { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "One number", test: (p) => /[0-9]/.test(p) },
  { label: "One special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const signInStyles = `
  .zs-back { border: 0; background: transparent; padding: 0; font-size: 13px; font-weight: 600; color: #71717a; display: inline-flex; align-items: center; gap: 6px; }
  .zs-back:hover { color: #18181b; }
  .zs-step { font-size: 11px; font-weight: 700; color: #0EA84A; background: #ecfdf3; border-radius: 999px; padding: 4px 10px; }
  .zs-otp { text-align: center; letter-spacing: 0.5em; font-size: 22px; font-family: ui-monospace, monospace; }
  .zs-rules { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 12px; font-size: 12px; }
  .zs-rule { color: #a1a1aa; display: flex; align-items: center; gap: 6px; }
  .zs-rule.ok { color: #0EA84A; font-weight: 600; }
  .zs-link { border: 0; background: transparent; padding: 0; }
`;

function PasswordInput({ value, onChange, placeholder, show, onToggle, disabled, autoFocus }) {
  return (
    <div className="position-relative">
      <i className="ki-outline ki-lock fs-5 position-absolute top-50 start-0 translate-middle-y ms-4 text-gray-400"></i>
      <input
        type={show ? "text" : "password"}
        placeholder={placeholder}
        autoComplete="new-password"
        className="d6m-input ps-12 pe-12"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoFocus={autoFocus}
      />
      {onToggle && (
        <button
          type="button"
          className="d6-icon-btn position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent"
          onClick={onToggle}
          aria-label={show ? "Hide password" : "Show password"}
        >
          <i className={`ki-outline ${show ? "ki-eye" : "ki-eye-slash"} fs-4 text-gray-500`}></i>
        </button>
      )}
    </div>
  );
}

// `waitSeconds` > 0 while the API has rate limited this step: the button stays disabled with a countdown.
function SubmitButton({ loading, waitSeconds = 0, children }) {
  return (
    <button type="submit" className="btn btn-success d6-mbtn w-100" style={{ height: 48 }} disabled={loading || waitSeconds > 0}>
      {waitSeconds > 0 && !loading ? (
        <span className="indicator-label">Try again in {waitSeconds}s</span>
      ) : !loading ? (
        <span className="indicator-label">{children}</span>
      ) : (
        <span className="indicator-progress d-inline-block">
          Please wait...
          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
        </span>
      )}
    </button>
  );
}

function PasswordRules({ password }) {
  return (
    <div className="zs-rules mb-6">
      {PASSWORD_RULES.map((r) => {
        const ok = r.test(password);
        return (
          <span key={r.label} className={`zs-rule ${ok ? "ok" : ""}`}>
            <i className={`ki-outline ${ok ? "ki-check-circle" : "ki-information-2"} fs-7`}></i>
            {r.label}
          </span>
        );
      })}
    </div>
  );
}

export default function SignIn() {
  const navigate = useNavigate();
  const [heroImage] = useState(pickSignInImage);

  useEffect(() => {
    try { sessionStorage.setItem(LAST_IMAGE_KEY, heroImage); } catch {}
  }, [heroImage]);

  // "login" | "setup" (tenant first-time password) | "forgot"
  const [view, setView] = useState("login");
  const [loading, setLoading] = useState(false);

  // Seconds left on any rate limit the API put on each step (0 when it may be retried).
  const loginWait = useRateLimit(/^POST \/auth\/(admin-login|tenant-login|primary-occupant-login)$/);
  const setupStartWait = useRateLimit(/^POST \/auth\/(tenant|primary-occupant)-login\/start$/);
  const setupVerifyWait = useRateLimit(/^POST \/auth\/(tenant|primary-occupant)-login\/verify-otp$/);
  const fpStartWait = useRateLimit("POST /auth/forgot-password/start");
  const fpVerifyWait = useRateLimit("POST /auth/forgot-password/verify-otp");

  // ── Login ──
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    try {
      const rem = localStorage.getItem("zangon_remember_me");
      return rem !== null ? rem === "true" : true;
    } catch {
      return true;
    }
  });

  // ── First-time setup (tenant) ──
  const [setupStep, setSetupStep] = useState(1);
  const [setupId, setSetupId] = useState("");
  const [setupOtp, setSetupOtp] = useState("");
  const [setupTenantId, setSetupTenantId] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [setupPass, setSetupPass] = useState("");
  const [setupConfirm, setSetupConfirm] = useState("");
  const [showSetupPass, setShowSetupPass] = useState(false);

  // ── Forgot password (tenant) ──
  const [fpStep, setFpStep] = useState(1);
  const [fpId, setFpId] = useState("");
  const [fpEstateId, setFpEstateId] = useState("");
  const [fpRequestId, setFpRequestId] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpToken, setFpToken] = useState("");
  const [fpPass, setFpPass] = useState("");
  const [fpConfirm, setFpConfirm] = useState("");
  const [showFpPass, setShowFpPass] = useState(false);

  useEffect(() => {
    try {
      if (getSession()?.accessToken) {
        navigate("/dashboard", { replace: true });
      } else if (isTenantAuthenticated()) {
        navigate("/tenant/dashboard", { replace: true });
      }
    } catch {}
    window.KTComponents?.init?.();
  }, [navigate]);

  const goTo = (next) => {
    setView(next);
    setSetupStep(1);
    setFpStep(1);
    setSetupOtp("");
    setFpOtp("");
  };

  // ─────────────────────────────────────────
  // Login
  // ─────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanId = identifier.trim();
    if (!cleanId || !password) {
      toast.error("Email and password are required.");
      return;
    }
    setLoading(true);
    try {
      const res = await toast.promise(
        unifiedLogin({ identifier: cleanId, password, rememberMe }),
        {
          loading: "Signing you in...",
          success: (r) => r?.message || "Login successful.",
          error: (err) => err?.message || "Login failed.",
        }
      );
      try {
        localStorage.setItem("zangon_remember_me", rememberMe ? "true" : "false");
        if (rememberMe) localStorage.setItem("zangon_remembered_email", cleanId);
        else localStorage.removeItem("zangon_remembered_email");
      } catch {}
      navigate(res.redirect, { replace: true });
    } catch (error) {
      console.error("Login Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────
  // First-time setup: identify → OTP → password
  // ─────────────────────────────────────────
  const handleSetupStart = async (e) => {
    e.preventDefault();
    if (!setupId.trim()) {
      toast.error("Enter your registered email or phone number.");
      return;
    }
    setLoading(true);
    try {
      const res = await tenantFirstLoginStart({ emailOrPhone: setupId });
      setSetupTenantId(res?.tenantId || "");
      if (res?.requiresPassword && !res?.requiresOtpSetup) {
        toast("You already have a password. Please sign in.", { icon: "ℹ️" });
        setIdentifier(setupId.trim());
        goTo("login");
        return;
      }
      toast.success(res?.message || "Verification code sent!");
      setSetupStep(2);
    } catch (err) {
      toast.error(err.message || "Could not start account setup.");
    } finally {
      setLoading(false);
    }
  };

  const handleSetupVerify = async (e) => {
    e.preventDefault();
    if (setupOtp.trim().length < 4) {
      toast.error("Enter the verification code you received.");
      return;
    }
    setLoading(true);
    try {
      const res = await tenantFirstLoginVerifyOtp({ emailOrPhone: setupId, otp: setupOtp });
      setSetupTenantId(res?.tenantId || setupTenantId);
      setSetupToken(res?.setupToken || "");
      toast.success("Code verified! Create your password.");
      setSetupStep(3);
    } catch (err) {
      toast.error(err.message || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  const handleSetupPassword = async (e) => {
    e.preventDefault();
    const ruleErr = validatePassword(setupPass);
    if (ruleErr) return toast.error(ruleErr);
    if (setupPass !== setupConfirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      const res = await tenantFirstLoginSetPassword({
        tenantId: setupTenantId,
        setupToken,
        newPassword: setupPass,
        confirmNewPassword: setupConfirm,
        rememberMe,
      });
      if (res?.session?.accessToken) {
        clearSession();
        toast.success("Password created! Welcome to your portal.");
        navigate("/tenant/dashboard", { replace: true });
      } else {
        toast.success("Password created! Please sign in.");
        setIdentifier(setupId.trim());
        goTo("login");
      }
    } catch (err) {
      toast.error(err.message || "Failed to set password.");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────
  // Forgot password: identify → OTP → reset
  // ─────────────────────────────────────────
  const handleFpStart = async (e) => {
    e.preventDefault();
    if (!fpId.trim()) return toast.error("Enter your registered email or phone number.");
    if (!fpEstateId.trim()) return toast.error("Enter your Estate ID.");
    setLoading(true);
    try {
      const res = await forgotPasswordStart({ userType: 0, identifier: fpId, estateId: fpEstateId });
      setFpRequestId(res?.resetRequestId || "");
      toast.success("Reset code sent!");
      setFpStep(2);
    } catch (err) {
      toast.error(err.message || "Failed to request a password reset.");
    } finally {
      setLoading(false);
    }
  };

  const handleFpVerify = async (e) => {
    e.preventDefault();
    if (!fpOtp.trim()) return toast.error("Enter the verification code.");
    setLoading(true);
    try {
      const res = await forgotPasswordVerifyOtp({ resetRequestId: fpRequestId, otp: fpOtp });
      setFpToken(res?.resetToken || "");
      toast.success("Code verified! Set your new password.");
      setFpStep(3);
    } catch (err) {
      toast.error(err.message || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  const handleFpReset = async (e) => {
    e.preventDefault();
    const ruleErr = validatePassword(fpPass);
    if (ruleErr) return toast.error(ruleErr);
    if (fpPass !== fpConfirm) return toast.error("Passwords do not match.");
    setLoading(true);
    try {
      await forgotPasswordReset({
        resetRequestId: fpRequestId,
        resetToken: fpToken,
        newPassword: fpPass,
        confirmNewPassword: fpConfirm,
      });
      toast.success("Password reset! Please sign in.");
      setIdentifier(fpId.trim());
      setPassword("");
      goTo("login");
    } catch (err) {
      toast.error(err.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────
  // Card header shared by setup / forgot views
  // ─────────────────────────────────────────
  const FlowHeader = ({ title, subtitle, step }) => (
    <>
      <div className="d-flex align-items-center justify-content-between mb-6">
        <button type="button" className="zs-back" onClick={() => goTo("login")} disabled={loading}>
          <i className="ki-outline ki-arrow-left fs-6"></i> Back to Sign In
        </button>
        <span className="zs-step">Step {step} of 3</span>
      </div>
      <div className="mb-7">
        <h1 className="d6-title text-gray-900 fs-2 mb-2">{title}</h1>
        <div className="d6m-muted fs-6">{subtitle}</div>
      </div>
    </>
  );

  const textField = ({ label, icon, value, onChange, placeholder, autoFocus, className = "mb-5", hint }) => (
    <div className={`d6m-field ${className}`}>
      <label>{label}</label>
      <div className="position-relative">
        <i className={`ki-outline ${icon} fs-5 position-absolute top-50 start-0 translate-middle-y ms-4 text-gray-400`}></i>
        <input
          type="text"
          placeholder={placeholder}
          autoComplete="off"
          className="d6m-input ps-12"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={loading}
          autoFocus={autoFocus}
        />
      </div>
      {hint && <div className="d6m-muted mt-2">{hint}</div>}
    </div>
  );

  const otpField = (value, onChange) => (
    <div className="d6m-field mb-6">
      <label>Verification Code</label>
      <input
        type="text"
        inputMode="numeric"
        maxLength={6}
        placeholder="••••••"
        className="d6m-input zs-otp"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
        disabled={loading}
        autoFocus
      />
    </div>
  );

  return (
    <>
      <Dashboard6Styles />
      <style>{signInStyles}</style>
      <div id="kt_app_root" className="d-flex flex-column flex-root min-vh-100" style={{ background: "#fafafa" }}>
        <div className="d-flex flex-column flex-lg-row flex-column-fluid min-vh-100">

          {/* BRAND SIDE */}
          <div className="d-none d-lg-flex flex-lg-row-fluid w-lg-50 order-1 p-6">
            <div
              className="position-relative w-100 d-flex flex-column justify-content-between p-10 overflow-hidden"
              style={{ borderRadius: 24, backgroundImage: `url(${heroImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
            >
              <div className="position-relative" style={{ zIndex: 1 }}>
                <img alt="Zangon" src="/assets/media/zangon-logo-white.svg" className="h-45px" />
              </div>
            </div>
          </div>

          {/* FORM SIDE */}
          <div className="d-flex flex-column flex-lg-row-fluid w-lg-50 p-6 p-lg-10 order-2 min-vh-100">
            <div className="d-flex flex-column flex-grow-1 justify-content-center align-items-center w-100">
              <div className="d6-card p-8 p-lg-10 w-100 mx-auto" style={{ maxWidth: 460 }}>

                {/* ── 1. SIGN IN ── */}
                {view === "login" && (
                  <form className="form w-100" noValidate onSubmit={handleSubmit}>
                    <div className="text-center mb-8">
                      <span className="d6m-head-icon mx-auto mb-5" style={{ width: 56, height: 56, borderRadius: 16 }}>
                        <img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" />
                      </span>
                      <h1 className="d6-title text-gray-900 fs-1 mb-2">Sign In</h1>
                      <div className="d6m-muted fs-6">Estate Management Portal</div>
                    </div>

                    {textField({
                      label: "Email",
                      icon: "ki-sms",
                      value: identifier,
                      onChange: setIdentifier,
                      placeholder: "you@estate.com",
                    })}

                    <div className="d6m-field mb-4">
                      <label>Password</label>
                      <PasswordInput
                        value={password}
                        onChange={setPassword}
                        placeholder="Password"
                        show={showPassword}
                        onToggle={() => setShowPassword((v) => !v)}
                        disabled={loading}
                      />
                    </div>

                    <div className="d-flex flex-stack flex-wrap gap-3 fw-semibold mb-7">
                      <label className="form-check form-check-custom form-check-solid cursor-pointer m-0">
                        <input
                          className="form-check-input h-20px w-20px"
                          type="checkbox"
                          name="remember"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          disabled={loading}
                        />
                        <span className="form-check-label text-gray-800 fs-7 fw-bold ms-2">Always remember me</span>
                      </label>
                      <button
                        type="button"
                        className="zs-link link-success fs-7 fw-bold"
                        onClick={() => {
                          setFpId(identifier.trim());
                          goTo("forgot");
                        }}
                      >
                        Forgot Password?
                      </button>
                    </div>

                    <SubmitButton loading={loading} waitSeconds={loginWait}>Sign In</SubmitButton>

                    <div className="d6m-muted text-center fs-7 mt-7">
                      First time here as a tenant?{" "}
                      <button
                        type="button"
                        className="zs-link link-success fw-bold"
                        onClick={() => {
                          setSetupId(identifier.trim());
                          goTo("setup");
                        }}
                      >
                        Set up your password
                      </button>
                    </div>
                    {/* <div className="d6m-muted text-center fs-7 mt-2">
                      Need an admin account?{" "}
                      <a href="https://pixels.com.ng" className="link-success fw-bold" target="_blank" rel="noreferrer">Contact System Support</a>
                    </div> */}
                  </form>
                )}

                {/* ── 2. FIRST-TIME TENANT SETUP ── */}
                {view === "setup" && (
                  <>
                    {setupStep === 1 && (
                      <form noValidate onSubmit={handleSetupStart}>
                        <FlowHeader step={1} title="Activate Your Account" subtitle="Enter the email or phone number your estate registered for you." />
                        {textField({
                          label: "Email or Phone Number",
                          icon: "ki-sms",
                          value: setupId,
                          onChange: setSetupId,
                          placeholder: "you@email.com or 0803 000 0000",
                          autoFocus: true,
                          className: "mb-7",
                        })}
                        <SubmitButton loading={loading} waitSeconds={setupStartWait}>Send Verification Code</SubmitButton>
                      </form>
                    )}

                    {setupStep === 2 && (
                      <form noValidate onSubmit={handleSetupVerify}>
                        <FlowHeader step={2} title="Verify It's You" subtitle={<>Enter the code sent to <span className="fw-bold text-gray-800">{setupId}</span>.</>} />
                        {otpField(setupOtp, setSetupOtp)}
                        <SubmitButton loading={loading} waitSeconds={setupVerifyWait}>Verify Code</SubmitButton>
                        <div className="text-center mt-5">
                          <button type="button" className="zs-back" onClick={() => setSetupStep(1)} disabled={loading}>Change email or phone</button>
                        </div>
                      </form>
                    )}

                    {setupStep === 3 && (
                      <form noValidate onSubmit={handleSetupPassword}>
                        <FlowHeader step={3} title="Create Password" subtitle="Choose a secure password for signing in to your portal." />
                        <div className="d6m-field mb-4">
                          <label>New Password</label>
                          <PasswordInput value={setupPass} onChange={setSetupPass} placeholder="New password" show={showSetupPass} onToggle={() => setShowSetupPass((v) => !v)} disabled={loading} autoFocus />
                        </div>
                        <div className="d6m-field mb-4">
                          <label>Confirm Password</label>
                          <PasswordInput value={setupConfirm} onChange={setSetupConfirm} placeholder="Re-enter password" show={showSetupPass} disabled={loading} />
                        </div>
                        <PasswordRules password={setupPass} />
                        <SubmitButton loading={loading}>Complete Setup &amp; Sign In</SubmitButton>
                      </form>
                    )}
                  </>
                )}

                {/* ── 3. FORGOT PASSWORD ── */}
                {view === "forgot" && (
                  <>
                    {fpStep === 1 && (
                      <form noValidate onSubmit={handleFpStart}>
                        <FlowHeader step={1} title="Reset Password" subtitle="We'll send a reset code to your registered email or phone." />
                        {textField({
                          label: "Email or Phone Number",
                          icon: "ki-sms",
                          value: fpId,
                          onChange: setFpId,
                          placeholder: "you@email.com or 0803 000 0000",
                          autoFocus: true,
                        })}
                        {textField({
                          label: "Estate ID",
                          icon: "ki-home-2",
                          value: fpEstateId,
                          onChange: setFpEstateId,
                          placeholder: "Provided by your estate",
                          className: "mb-7",
                        })}
                        <SubmitButton loading={loading} waitSeconds={fpStartWait}>Send Reset Code</SubmitButton>
                      </form>
                    )}

                    {fpStep === 2 && (
                      <form noValidate onSubmit={handleFpVerify}>
                        <FlowHeader step={2} title="Enter Reset Code" subtitle={<>Enter the code sent to <span className="fw-bold text-gray-800">{fpId}</span>.</>} />
                        {otpField(fpOtp, setFpOtp)}
                        <SubmitButton loading={loading} waitSeconds={fpVerifyWait}>Verify Code</SubmitButton>
                        <div className="text-center mt-5">
                          <button type="button" className="zs-back" onClick={() => setFpStep(1)} disabled={loading}>Change email or phone</button>
                        </div>
                      </form>
                    )}

                    {fpStep === 3 && (
                      <form noValidate onSubmit={handleFpReset}>
                        <FlowHeader step={3} title="New Password" subtitle="Set a new password for your account." />
                        <div className="d6m-field mb-4">
                          <label>New Password</label>
                          <PasswordInput value={fpPass} onChange={setFpPass} placeholder="New password" show={showFpPass} onToggle={() => setShowFpPass((v) => !v)} disabled={loading} autoFocus />
                        </div>
                        <div className="d6m-field mb-4">
                          <label>Confirm Password</label>
                          <PasswordInput value={fpConfirm} onChange={setFpConfirm} placeholder="Re-enter password" show={showFpPass} disabled={loading} />
                        </div>
                        <PasswordRules password={fpPass} />
                        <SubmitButton loading={loading}>Save Password</SubmitButton>
                      </form>
                    )}
                  </>
                )}

              </div>
            </div>

            <div className="d-flex justify-content-center px-10 pb-2 mt-auto pt-6">
              <div className="fw-semibold text-gray-400 text-center fs-7">
                2026&copy; <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary">Powered by Pixels Solutions Limited</a>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
